// src/components/DoctorConsultationWithDC.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { submitTechnicianServiceDone } from "./api";
import {
  listDoctorConsultations,
  getDoctorConsultation,
  createDoctorConsultation,
  updateDoctorConsultation,
  resolveReportUrl,
} from "../utils/doctorconsultationapidc";

const SERVICE_ID = 35;

// Editable fields that exist on the DoctorConsultation model / serializer
const EDITABLE_FIELDS = [
  "has_medical_conditions",
  "medical_conditions",
  "has_medications",
  "medications",
  "has_allergies",
  "allergies",
  "chief_complaint",
  "history",
  "diagnostic_tests",
  "advice",
];

const EMPTY_FORM = {
  has_medical_conditions: "No",
  medical_conditions: "",
  has_medications: "No",
  medications: "",
  has_allergies: "No",
  allergies: "",
  chief_complaint: "",
  history: "",
  diagnostic_tests: "",
  advice: "",
};

// ---------- helpers ----------
const normYesNo = (v) =>
  v === true || String(v ?? "").trim().toLowerCase() === "yes" ? "Yes" : "No";

// Build form values from a backend record. Keys missing from `rec` fall back to `base`.
const recordToForm = (rec, base = EMPTY_FORM) => {
  const out = { ...base };
  EDITABLE_FIELDS.forEach((key) => {
    if (rec && rec[key] !== undefined) {
      if (key.startsWith("has_")) out[key] = normYesNo(rec[key]);
      else out[key] = rec[key] ?? "";
    }
  });
  return out;
};

// Every identifier a record might expose for its patient (response shape can vary:
// flat fields or a nested `patient` object).
const recordPatientKeys = (rec) => {
  const vals = [];
  const p = rec?.patient;
  if (p && typeof p === "object") {
    vals.push(p.unique_patient_id, p.patient_unique_id, p.excel_patient_id, p.patient_excel_id);
  } else if (p !== undefined && p !== null) {
    vals.push(p);
  }
  vals.push(
    rec?.patient_unique_id,
    rec?.patient_excel_id,
    rec?.unique_patient_id,
    rec?.excel_patient_id
  );
  return vals
    .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
    .map((v) => String(v).trim().toLowerCase());
};

const recordLabel = (rec) => {
  const raw = rec.created_at || rec.updated_at || rec.date || rec.created;
  const d = raw ? new Date(raw) : null;
  const when = d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : "";
  return `Consultation #${rec.id}${when ? ` — ${when}` : ""}`;
};

// ---------- small UI pieces ----------
const inputBase =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 " +
  "focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100";

const Card = ({ icon, iconBg, title, children }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
    <header className="mb-5 flex items-center gap-3">
      <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBg}`}>{icon}</span>
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
    </header>
    <div className="space-y-5">{children}</div>
  </section>
);

const FieldError = ({ message }) =>
  message ? <p className="mt-1 text-xs text-red-600">{message}</p> : null;

const RadioGroup = ({ name, value, options, onChange, disabled }) => (
  <div className="flex flex-wrap gap-5" role="radiogroup">
    {options.map((opt) => (
      <label key={opt} className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
        <input
          type="radio"
          name={name}
          value={opt}
          checked={value === opt}
          onChange={() => onChange(opt)}
          disabled={disabled}
          className="h-4 w-4 cursor-pointer accent-blue-600"
        />
        {opt}
      </label>
    ))}
  </div>
);

const YesNoWithDetails = ({
  label, name, value, details, placeholder, onToggle, onDetails, disabled, error,
}) => (
  <div>
    <p className="mb-2 text-sm font-medium text-gray-700">{label}</p>
    <RadioGroup name={name} value={value} options={["Yes", "No"]} onChange={onToggle} disabled={disabled} />
    {value === "Yes" && (
      <div className="mt-3">
        <textarea
          rows={3}
          value={details}
          onChange={(e) => onDetails(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={inputBase}
        />
        <FieldError message={error} />
      </div>
    )}
  </div>
);

const TextAreaField = ({ label, value, onChange, placeholder, disabled, rows = 3 }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-gray-700">{label}</label>
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className={inputBase}
    />
  </div>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
    <path d="M12 11v6M9 14h6" />
  </svg>
);

const MedicalIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4v5a5 5 0 0 0 10 0V4" />
    <path d="M9 14v2a4 4 0 0 0 8 0v-1" />
    <circle cx="17" cy="12" r="2" />
  </svg>
);

// ---------- page ----------
function DoctorConsultationWithDC() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  const {
    patientId,
    patientExcelId,
    patientName,
    technicianId,
    campId,
    campLocation,
    packageId,
    packageName,
    assignmentId,
    serviceId,
    serviceName,
    isEnabled,
    isEditing: serviceCompleted,
  } = state;

  const [form, setForm] = useState(EMPTY_FORM);
  const [original, setOriginal] = useState(EMPTY_FORM); // snapshot used to compute PATCH diff
  const [consultations, setConsultations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // {type: 'success'|'error'|'warning', text}
  const [fieldErrors, setFieldErrors] = useState({});
  const submitLock = useRef(false);

  // Required context check
  const missingContext = useMemo(() => {
    if (!patientId || !technicianId || !assignmentId) return true;
    if (Number(serviceId) !== SERVICE_ID) return true;
    if (isEnabled !== true) return true;
    return false;
  }, [patientId, technicianId, assignmentId, serviceId, isEnabled]);

  const goBack = useCallback(() => {
    if (packageId && technicianId) {
      navigate("/patient-dashboard", {
        state: { packageId, technicianId, campId, packageName },
      });
    } else {
      navigate(-1);
    }
  }, [navigate, packageId, technicianId, campId, packageName]);

  const applyRecord = useCallback((rec) => {
    const values = recordToForm(rec);
    setForm(values);
    setOriginal(values);
    setSelectedId(rec.id ?? null);
    setPdfUrl(resolveReportUrl(rec.pdf_report));
    setFieldErrors({});
  }, []);

  // ---- load existing consultation(s) ----
  useEffect(() => {
    if (missingContext) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const all = await listDoctorConsultations();
        if (cancelled) return;

        const wanted = [patientId, patientExcelId]
          .filter(Boolean)
          .map((v) => String(v).trim().toLowerCase());

        const mine = all
          .filter((rec) => recordPatientKeys(rec).some((k) => wanted.includes(k)))
          .sort((a, b) => Number(b.id) - Number(a.id)); // newest first

        setConsultations(mine);
        if (mine.length > 0) {
          applyRecord(mine[0]);
        } else {
          setForm(EMPTY_FORM);
          setOriginal(EMPTY_FORM);
          setSelectedId(null);
          setPdfUrl("");
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Failed to load consultation.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [missingContext, patientId, patientExcelId, applyRecord]);

  // ---- form handlers ----
  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const setYesNo = (flagKey, detailKey, value) => {
    setForm((prev) => ({
      ...prev,
      [flagKey]: value,
      // "No" clears the details so stale text is never saved
      [detailKey]: value === "No" ? "" : prev[detailKey],
    }));
    setFieldErrors((prev) => ({ ...prev, [detailKey]: undefined }));
  };

  const handleSelectConsultation = (e) => {
    const rec = consultations.find((c) => String(c.id) === e.target.value);
    if (rec) {
      setNotice(null);
      applyRecord(rec);
    }
  };

  // ---- validation ----
  const validate = () => {
    const errs = {};
    if (form.has_medical_conditions === "Yes" && !form.medical_conditions.trim())
      errs.medical_conditions = "Specify the medical conditions.";
    if (form.has_medications === "Yes" && !form.medications.trim())
      errs.medications = "Specify the current medications.";
    if (form.has_allergies === "Yes" && !form.allergies.trim())
      errs.allergies = "Specify the allergies.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ---- save ----
  const handleSave = async () => {
    if (submitLock.current || saving) return;
    setNotice(null);
    if (!validate()) {
      setNotice({ type: "error", text: "Please fix the highlighted fields." });
      return;
    }

    const isUpdate = selectedId !== null;
    let payload;

    if (isUpdate) {
      payload = {};
      EDITABLE_FIELDS.forEach((key) => {
        if (form[key] !== original[key]) payload[key] = form[key];
      });
      if (Object.keys(payload).length === 0) {
        setNotice({ type: "warning", text: "No changes to save." });
        return;
      }
    } else {
      payload = {
        patient_unique_id: patientId,
        patient_excel_id: patientExcelId || "",
        technician_id: Number(technicianId),
        ...Object.fromEntries(EDITABLE_FIELDS.map((k) => [k, form[k]])),
      };
    }

    submitLock.current = true;
    setSaving(true);
    try {
      const res = isUpdate
        ? await updateDoctorConsultation(selectedId, payload)
        : await createDoctorConsultation(payload);

      const newId = res?.id ?? selectedId;
      if (newId === undefined || newId === null) {
        throw new Error("Saved, but the server did not return a consultation ID.");
      }

      // Re-read the record so we show the server's version (and the generated PDF link)
      let saved = res;
      try {
        saved = await getDoctorConsultation(newId);
      } catch {
        /* keep the POST/PATCH response */
      }

      const values = recordToForm(saved, form);
      setForm(values);
      setOriginal(values);
      setSelectedId(newId);
      setPdfUrl(resolveReportUrl(saved?.pdf_report ?? res?.pdf_report));
      setConsultations((prev) => {
        const merged = { ...(prev.find((c) => c.id === newId) || {}), ...saved, id: newId };
        const others = prev.filter((c) => c.id !== newId);
        return [merged, ...others].sort((a, b) => Number(b.id) - Number(a.id));
      });

      let text = isUpdate ? "Consultation updated successfully." : "Consultation saved successfully.";
      let type = "success";

      // Only mark the service complete through the existing completion API,
      // and only when the dashboard says it is not already completed.
      // Trigger the technician submit API after successfully saving
      // the doctor consultation, whether it was created or updated.
        if (!serviceCompleted) {
        try {
            await submitTechnicianServiceDone({
            patientId,
            technicianId,
            serviceId: SERVICE_ID,
            });
        } catch (doneErr) {
            type = "warning";
            text += ` However, the service could not be marked as completed: ${
            doneErr.message || "Unknown error"
            }`;
        }
        }
      setNotice({ type, text });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to save consultation." });
    } finally {
      submitLock.current = false;
      setSaving(false);
    }
  };

  // ---------- render ----------
  if (missingContext) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <p className="mb-2 font-semibold text-red-700">Cannot open this service</p>
          <p className="mb-5 text-sm text-gray-600">
            Patient, technician or service assignment details are missing, or this service is disabled.
            Open it again from the patient dashboard.
          </p>
          <button
            onClick={goBack}
            className="rounded-lg bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-800"
          >
            Back to Patients
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="flex items-center gap-3 text-gray-600">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          Loading consultation...
        </div>
      </div>
    );
  }

  const isUpdate = selectedId !== null;
  const noticeStyles = {
    success: "border-green-300 bg-green-50 text-green-800",
    error: "border-red-300 bg-red-50 text-red-800",
    warning: "border-yellow-300 bg-yellow-50 text-yellow-800",
  };

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-6 sm:py-8">
      <div className="mx-auto max-w-3xl space-y-5">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">
              {serviceName || "Doctor Consultation with DC"}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {patientName || "Patient"}
              {patientExcelId ? ` · ${patientExcelId}` : ""}
              {campLocation ? ` · ${campLocation}` : ""}
              {campId ? ` (Camp #${campId})` : ""}
            </p>
          </div>
          <button
            onClick={goBack}
            className="self-start rounded-lg bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-800"
          >
            ← Back to Patients
          </button>
        </div>

        {loadError && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
            {loadError} You can still fill the form, but check for an existing consultation before saving to avoid duplicates.
          </div>
        )}

        {serviceCompleted && !isUpdate && !loadError && (
          <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            This service is marked complete but no saved consultation was found for this patient. Saving will create one.
          </div>
        )}

        {notice && (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${noticeStyles[notice.type]}`}
            role="alert"
          >
            {notice.text}
          </div>
        )}

        {/* Consultation history */}
        {consultations.length > 1 && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              This patient has {consultations.length} consultations. Select one to view or edit:
            </label>
            <select
              value={selectedId ?? ""}
              onChange={handleSelectConsultation}
              disabled={saving}
              className={inputBase}
            >
              {consultations.map((c) => (
                <option key={c.id} value={c.id}>
                  {recordLabel(c)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Section A */}
        <Card icon={<DocumentIcon />} iconBg="bg-green-100" title="Medical History">
          <YesNoWithDetails
            label="Do you have any existing medical conditions?"
            name="has_medical_conditions"
            value={form.has_medical_conditions}
            details={form.medical_conditions}
            placeholder="Specify medical conditions..."
            onToggle={(v) => setYesNo("has_medical_conditions", "medical_conditions", v)}
            onDetails={(v) => setField("medical_conditions", v)}
            disabled={saving}
            error={fieldErrors.medical_conditions}
          />
          <YesNoWithDetails
            label="Are you currently taking any medications?"
            name="has_medications"
            value={form.has_medications}
            details={form.medications}
            placeholder="Specify current medications..."
            onToggle={(v) => setYesNo("has_medications", "medications", v)}
            onDetails={(v) => setField("medications", v)}
            disabled={saving}
            error={fieldErrors.medications}
          />
          <YesNoWithDetails
            label="Do you have any allergies?"
            name="has_allergies"
            value={form.has_allergies}
            details={form.allergies}
            placeholder="Specify allergies..."
            onToggle={(v) => setYesNo("has_allergies", "allergies", v)}
            onDetails={(v) => setField("allergies", v)}
            disabled={saving}
            error={fieldErrors.allergies}
          />
        </Card>

        {/* Section B */}
        <Card icon={<MedicalIcon />} iconBg="bg-purple-100" title="Consultation Details">
          <TextAreaField
            label="Chief Complaint"
            value={form.chief_complaint}
            onChange={(v) => setField("chief_complaint", v)}
            placeholder="Enter chief complaint..."
            disabled={saving}
          />
          <TextAreaField
            label="History (Family/Own)"
            value={form.history}
            onChange={(v) => setField("history", v)}
            placeholder="Enter family or personal history..."
            disabled={saving}
          />
          <TextAreaField
            label="Diagnostic Tests Recommended"
            value={form.diagnostic_tests}
            onChange={(v) => setField("diagnostic_tests", v)}
            placeholder="Enter recommended diagnostic tests..."
            disabled={saving}
          />
          <TextAreaField
            label="Advice (Tests/Medicine)"
            value={form.advice}
            onChange={(v) => setField("advice", v)}
            placeholder="Enter advice..."
            disabled={saving}
          />
        </Card>

        {/* Actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {saving
              ? isUpdate ? "Updating..." : "Saving..."
              : isUpdate ? "Update Consultation" : "Save Consultation"}
          </button>

          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-lg border border-green-600 bg-white px-5 py-2.5 text-sm font-medium text-green-700 hover:bg-green-50"
            >
              View PDF Report
            </a>
          )}

          <button
            onClick={goBack}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Back to Patients
          </button>
        </div>
      </div>
    </div>
  );
}

export default DoctorConsultationWithDC;